import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AIPromptAssistant from '../components/AIPromptAssistant';

const ImageGenerationStudio = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPromptAssistant, setShowPromptAssistant] = useState(false);

  // Generation parameters
  const [formData, setFormData] = useState({
    prompt: '',
    negative_prompt: '',
    width: 1024,
    height: 1024,
    num_outputs: 1,
    cfg_scale: 7.0,
    steps: 30,
    sampler: 'dpm++_2m',
    seed: '',
    style_preset: '',
    provider: 'stability_ai',
    model_id: ''
  });

  const providers = [
    { value: 'stability_ai', label: 'Stability AI', models: ['stable-diffusion-xl-1024-v1-0', 'stable-diffusion-v1-6'] },
    { value: 'replicate', label: 'Replicate', models: ['stability-ai/sdxl'] },
    { value: 'openai_dalle', label: 'OpenAI DALL-E', models: ['dall-e-3', 'dall-e-2'] },
    { value: 'self_hosted', label: 'Self-Hosted', models: [] }
  ];

  const stylePresets = [
    { value: '', label: 'None' },
    { value: 'photographic', label: 'Photographic' },
    { value: 'digital-art', label: 'Digital Art' },
    { value: 'cinematic', label: 'Cinematic' },
    { value: 'anime', label: 'Anime' },
    { value: 'fantasy-art', label: 'Fantasy Art' },
    { value: 'neon-punk', label: 'Neon Punk' },
    { value: 'isometric', label: 'Isometric' },
    { value: '3d-model', label: '3D Model' },
    { value: 'pixel-art', label: 'Pixel Art' },
    { value: 'line-art', label: 'Line Art' },
    { value: 'origami', label: 'Origami' },
    { value: 'low-poly', label: 'Low Poly' },
    { value: 'tile-texture', label: 'Tile Texture' }
  ];

  const aspectRatios = [
    { label: '1:1', width: 1024, height: 1024 },
    { label: '16:9', width: 1024, height: 576 },
    { label: '9:16', width: 576, height: 1024 },
    { label: '4:3', width: 1024, height: 768 },
    { label: '3:4', width: 768, height: 1024 },
    { label: '21:9', width: 1024, height: 440 },
  ];

  const samplers = [
    'dpm++_2m', 'dpm++_2s_ancestral', 'dpm++_sde', 'euler', 'euler_ancestral', 
    'ddim', 'ddpm', 'k_euler', 'k_euler_ancestral', 'k_lms'
  ];

  // Fetch generated images
  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/media/assets', {
        params: { type: 'image', per_page: 50 }
      });
      setGeneratedImages(response.data.items || []);
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Poll for job status
  useEffect(() => {
    if (jobs.length === 0) return;

    const pendingJobs = jobs.filter(j => j.status !== 'completed' && j.status !== 'failed');
    if (pendingJobs.length === 0) return;

    const interval = setInterval(async () => {
      for (const job of pendingJobs) {
        try {
          const response = await api.get(`/media/jobs/${job.job_id}`);
          setJobs(prev => prev.map(j => 
            j.job_id === job.job_id ? { ...j, ...response.data } : j
          ));
          
          if (response.data.status === 'completed') {
            setSuccess('Image generated successfully!');
            fetchImages();
          } else if (response.data.status === 'failed') {
            setError(`Generation failed: ${response.data.error}`);
          }
        } catch (err) {
          console.error('Error polling job:', err);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobs, fetchImages]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleAspectRatio = (ratio) => {
    setFormData(prev => ({
      ...prev,
      width: ratio.width,
      height: ratio.height
    }));
  };

  const handleGenerate = async () => {
    if (!formData.prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const params = {
        prompt: formData.prompt,
        negative_prompt: formData.negative_prompt || undefined,
        width: formData.width,
        height: formData.height,
        num_outputs: formData.num_outputs,
        cfg_scale: formData.cfg_scale,
        steps: formData.steps,
        sampler: formData.sampler || undefined,
        seed: formData.seed ? parseInt(formData.seed) : undefined,
        style_preset: formData.style_preset || undefined,
        provider: formData.provider,
        model_id: formData.model_id || undefined
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await api.post('/media/generate/image', params);
      
      setJobs(prev => [...prev, {
        job_id: response.data.job_id,
        status: response.data.status,
        prompt: formData.prompt
      }]);
      
      setSuccess('Generation started! Your image will appear shortly.');
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err.response?.data?.error || 'Failed to start generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleRandomSeed = () => {
    const seed = Math.floor(Math.random() * 2147483647);
    setFormData(prev => ({ ...prev, seed: seed.toString() }));
  };

  const currentAspect = aspectRatios.find(r => r.width === formData.width && r.height === formData.height);
  const currentProvider = providers.find(p => p.value === formData.provider);

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Image Generation</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #8B5CF6'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🎨 Image Generation Studio
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Create AI-generated images using various providers and models
            </p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {success}
          </div>
        )}

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr 320px',
          gap: '1.5rem',
          minHeight: '600px'
        }}>
          {/* Left Panel - Controls */}
          <div style={{
            backgroundColor: 'var(--gray-50)',
            borderRadius: '8px',
            border: '1px solid var(--gray-200)',
            padding: '1rem'
          }}>

            {/* Prompt */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Prompt *</label>
                <button
                  type="button"
                  onClick={() => setShowPromptAssistant(true)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    color: '#8b5cf6',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ✨ AI Prompt Assistant
                </button>
              </div>
              <textarea
                name="prompt"
                value={formData.prompt}
                onChange={handleInputChange}
                placeholder="Describe the image you want to generate..."
                className="form-control"
                rows={4}
              />
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>{formData.prompt.length} / 2000 characters</div>
            </div>

            {/* Negative Prompt */}
            <div className="form-group">
              <label className="form-label">Negative Prompt</label>
              <textarea
                name="negative_prompt"
                value={formData.negative_prompt}
                onChange={handleInputChange}
                placeholder="What to avoid in the image..."
                className="form-control"
                rows={2}
              />
            </div>

            {/* Provider */}
            <div className="form-group">
              <label className="form-label">Provider</label>
              <select
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                className="form-select"
              >
                {providers.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            {/* Model */}
            {currentProvider?.models.length > 0 && (
              <div className="form-group">
                <label className="form-label">Model</label>
                <select
                  name="model_id"
                  value={formData.model_id}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Default</option>
                  {currentProvider.models.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Aspect Ratio */}
            <div className="form-group">
              <label className="form-label">Aspect Ratio</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {aspectRatios.map(ratio => (
                  <button
                    key={ratio.label}
                    type="button"
                    onClick={() => handleAspectRatio(ratio)}
                    className={currentAspect?.label === ratio.label ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>{formData.width} x {formData.height}</div>
            </div>

            {/* Style Preset */}
            <div className="form-group">
              <label className="form-label">Style Preset</label>
              <select
                name="style_preset"
                value={formData.style_preset}
                onChange={handleInputChange}
                className="form-select"
              >
                {stylePresets.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Number of Images */}
            <div className="form-group">
              <label className="form-label">Number of Images: {formData.num_outputs}</label>
              <input
                type="range"
                name="num_outputs"
                min="1"
                max="4"
                value={formData.num_outputs}
                onChange={handleInputChange}
                className="form-range"
                style={{ width: '100%' }}
              />
            </div>

            {/* Advanced Toggle */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
                cursor: 'pointer',
                borderTop: '1px solid var(--gray-200)',
                marginTop: '1rem'
              }}
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--gray-600)' }}>
                ⚙️ Advanced Settings
              </span>
              <span>{showAdvanced ? '▲' : '▼'}</span>
            </div>

            {showAdvanced && (
              <>
                {/* CFG Scale */}
                <div className="form-group">
                  <label className="form-label">CFG Scale: {formData.cfg_scale}</label>
                  <input
                    type="range"
                    name="cfg_scale"
                    min="1"
                    max="20"
                    step="0.5"
                    value={formData.cfg_scale}
                    onChange={handleInputChange}
                    className="form-range"
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>Higher values = more prompt adherence</div>
                </div>

                {/* Steps */}
                <div className="form-group">
                  <label className="form-label">Steps: {formData.steps}</label>
                  <input
                    type="range"
                    name="steps"
                    min="10"
                    max="100"
                    value={formData.steps}
                    onChange={handleInputChange}
                    className="form-range"
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>More steps = higher quality (slower)</div>
                </div>

                {/* Sampler */}
                <div className="form-group">
                  <label className="form-label">Sampler</label>
                  <select
                    name="sampler"
                    value={formData.sampler}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {samplers.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Seed */}
                <div className="form-group">
                  <label className="form-label">Seed</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      name="seed"
                      value={formData.seed}
                      onChange={handleInputChange}
                      placeholder="Random"
                      className="form-control"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleRandomSeed}
                      className="btn btn-secondary btn-sm"
                    >
                      🎲 Random
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Generate Button */}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || !formData.prompt.trim()}
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '1rem', padding: '12px' }}
            >
              {generating ? (
                <>
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }}></span>
                  Generating...
                </>
              ) : (
                <>🎨 Generate Image</>
              )}
            </button>

            {/* Active Jobs */}
            {jobs.filter(j => j.status !== 'completed').length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <label className="form-label">Active Jobs</label>
                {jobs.filter(j => j.status !== 'completed').map(job => (
                  <div key={job.job_id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    fontSize: '12px'
                  }}>
                    <span style={{ 
                      maxWidth: '150px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {job.prompt.substring(0, 30)}...
                    </span>
                    <span className={`badge badge-${job.status === 'generating' ? 'warning' : job.status === 'completed' ? 'success' : 'secondary'}`}>
                      {job.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Center - Gallery */}
          <div style={{
            backgroundColor: 'var(--gray-50)',
            borderRadius: '8px',
            border: '1px solid var(--gray-200)',
            padding: '1rem',
            overflowY: 'auto'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
              🖼️ Generated Images ({generatedImages.length})
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <span className="spinner-border"></span>
                <div style={{ marginTop: '1rem', color: 'var(--gray-500)' }}>Loading images...</div>
              </div>
            ) : generatedImages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🖼️</div>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--gray-700)' }}>No images yet</div>
                <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                  Enter a prompt and click Generate to create your first image
                </div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '1rem'
              }}>
                {generatedImages.map(img => (
                  <div
                    key={img.id}
                    style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      aspectRatio: '1',
                      backgroundColor: 'var(--gray-100)',
                      border: selectedImage?.id === img.id ? '2px solid var(--primary-color)' : '1px solid var(--gray-200)'
                    }}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={img.file_url || img.thumbnail_url}
                      alt={img.title || 'Generated image'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '8px',
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color: 'white',
                      fontSize: '11px'
                    }}>
                      {img.prompt?.substring(0, 40)}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Details */}
          <div style={{
            backgroundColor: 'var(--gray-50)',
            borderRadius: '8px',
            border: '1px solid var(--gray-200)',
            padding: '1rem',
            overflowY: 'auto'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
              Image Details
            </div>
            {selectedImage ? (
              <>
                <img
                  src={selectedImage.file_url || selectedImage.thumbnail_url}
                  alt={selectedImage.title}
                  style={{ width: '100%', borderRadius: '8px', marginBottom: '1rem' }}
                />
                
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Dimensions</span>
                  <span style={{ fontWeight: '500' }}>{selectedImage.width} x {selectedImage.height}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Provider</span>
                  <span style={{ fontWeight: '500' }}>{selectedImage.provider || selectedImage.generator}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Model</span>
                  <span style={{ fontWeight: '500' }}>{selectedImage.model_version || 'Default'}</span>
                </div>
                  
                  {selectedImage.seed && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                      <span style={{ color: 'var(--gray-500)' }}>Seed</span>
                      <span style={{ fontWeight: '500' }}>{selectedImage.seed}</span>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                    <span style={{ color: 'var(--gray-500)' }}>Created</span>
                    <span style={{ fontWeight: '500' }}>
                      {selectedImage.created_at ? new Date(selectedImage.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  
                  {selectedImage.api_cost && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', fontSize: '14px' }}>
                      <span style={{ color: 'var(--gray-500)' }}>Cost</span>
                      <span style={{ fontWeight: '500' }}>${selectedImage.api_cost.toFixed(4)}</span>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '1rem' }}>
                    <label className="form-label">Prompt</label>
                    <p style={{ fontSize: '14px', color: 'var(--gray-600)', backgroundColor: 'white', padding: '8px', borderRadius: '6px' }}>
                      {selectedImage.prompt}
                    </p>
                  </div>
                  
                  {selectedImage.negative_prompt && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <label className="form-label">Negative Prompt</label>
                      <p style={{ fontSize: '14px', color: 'var(--gray-600)', backgroundColor: 'white', padding: '8px', borderRadius: '6px' }}>
                        {selectedImage.negative_prompt}
                      </p>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => window.open(selectedImage.file_url, '_blank')}
                    >
                      📥 Download
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1 }}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          prompt: selectedImage.prompt,
                          negative_prompt: selectedImage.negative_prompt || '',
                          width: selectedImage.width,
                          height: selectedImage.height,
                          seed: selectedImage.seed?.toString() || ''
                        }));
                      }}
                    >
                      🔄 Reuse
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👆</div>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--gray-700)' }}>Select an image</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                    Click on an image to view details
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* AI Prompt Assistant Modal */}
      {showPromptAssistant && (
        <AIPromptAssistant
          initialTopic={formData.prompt}
          onClose={() => setShowPromptAssistant(false)}
          onSelectPrompt={(data) => {
            setFormData(prev => ({
              ...prev,
              prompt: data.prompt,
              negative_prompt: data.negative_prompt || prev.negative_prompt,
              width: data.width || prev.width,
              height: data.height || prev.height,
              style_preset: data.style_preset || prev.style_preset
            }));
            setShowPromptAssistant(false);
          }}
        />
      )}
    </div>
  );
};

export default ImageGenerationStudio;
